import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Check, X, FileText } from "lucide-react";
import { Section, StatusBadge, TeamLogo } from "../components/UIBits";
import { useT, formatDayLocalized } from "../lib/i18n";
import { phaseLabel } from "../lib/constants";
import api from "../lib/api";

export default function MatchPage() {
    const { t, lang } = useT();
    const { num } = useParams();
    const [game, setGame] = useState(null);
    const [teams, setTeams] = useState([]);
    const [players, setPlayers] = useState([]);
    const [attendance, setAttendance] = useState([]);
    const [stats, setStats] = useState([]);
    const [settings, setSettings] = useState({ stats_public: true });
    const [events, setEvents] = useState([]);

    const load = () => {
        api.get(`/games/${num}`).then(r => setGame(r.data)).catch(() => {});
        api.get(`/teams?light=true`).then(r => setTeams(r.data));
        api.get(`/players`).then(r => setPlayers(r.data));
        api.get(`/attendance/${num}`).then(r => setAttendance(r.data));
        api.get(`/player_stats/${num}`).then(r => setStats(r.data));
        api.get(`/settings`).then(r => setSettings(r.data));
        api.get(`/games/${num}/events`).then(r => setEvents(r.data)).catch(() => {});
    };
    // Auto-refresh every 5s while game is LIVE (bump interval as needed)
    useEffect(() => {
        load();
        const period = game && game.status === "live" ? 5000 : 12000;
        const i = setInterval(load, period);
        return () => clearInterval(i);
    }, [num, game?.status]); // eslint-disable-line

    if (!game) return <Section><div className="text-white/50">{t("common.loading")}</div></Section>;

    const teamMap = Object.fromEntries(teams.map(t => [t.id, t]));
    const home = teamMap[game.home_team_id];
    const away = teamMap[game.away_team_id];
    const homePlayers = players.filter(p => p.team_id === game.home_team_id);
    const awayPlayers = players.filter(p => p.team_id === game.away_team_id);
    const playerMap = Object.fromEntries(players.map(p => [p.id, p]));
    const att = (pid) => attendance.find(a => a.player_id === pid)?.present;
    const stat = (pid) => stats.find(s => s.player_id === pid) || { goals: 0, assists: 0, pim: 0 };

    const TeamSide = ({ team, placeholder, side, list, score }) => (
        <div className={`glass-card p-6 ${side === "away" ? "border-r-4" : "border-l-4"}`} style={{ borderColor: team?.color || "#4FE0D5" }}>
            <div className="flex items-center gap-4 mb-4">
                <TeamLogo team={team} size={56} />
                <div className="flex-1 min-w-0">
                    <div className="font-display text-3xl uppercase tracking-wide truncate">{team?.name || placeholder}</div>
                    {placeholder && team && <div className="text-xs text-white/40">({placeholder})</div>}
                </div>
                <div className="font-display text-6xl text-[#7FD93C]">{score}</div>
            </div>
            <div className="grid gap-2">
                {list.length === 0 && <div className="text-white/40 text-sm">{t("teams.no_roster")}</div>}
                {list.map(p => {
                    const s = stat(p.id);
                    const present = att(p.id);
                    return (
                        <div key={p.id} className="flex items-center gap-3 py-2 border-b border-white/5">
                            <div className="font-display text-2xl text-[#4FE0D5] w-10">#{p.number}</div>
                            <div className="flex-1 truncate">
                                <div className="text-sm">{p.name}</div>
                            </div>
                            {present !== undefined ? (
                                present
                                    ? <span title="Présent" className="text-[#7FD93C]"><Check size={16} /></span>
                                    : <span title="Absent" className="text-white/30"><X size={16} /></span>
                            ) : null}
                            {settings.stats_public && (s.goals || s.assists || s.pim) ? (
                                <div className="text-xs text-white/60 font-mono">
                                    {s.goals}B {s.assists}A {s.pim}PIM
                                </div>
                            ) : null}
                        </div>
                    );
                })}
            </div>
        </div>
    );

    return (
        <div data-testid="match-page">
            <Helmet><title>{`Partie #${game.game_num} — Défi Ultime`}</title></Helmet>
            <Section>
                <Link to="/horaire" className="inline-flex items-center gap-2 text-[#4FE0D5] mb-6 hover:text-[#7FD93C]"><ArrowLeft size={16} /> {t("nav.schedule")}</Link>

                <div className="glass-card p-6 mb-8">
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                        <div>
                            <div className="label-mini flex items-center gap-2 flex-wrap">
                                <span>#{game.game_num} • {game.category}</span>
                                {game.phase && game.phase !== "preliminaire" && (
                                    <span className="px-2 py-0.5 angle-tag bg-[#4FE0D5]/15 text-[#4FE0D5] font-bold" data-testid="match-phase">{phaseLabel(game.phase, lang)}</span>
                                )}
                                {game.status === "live" && (
                                    <span className="px-2 py-0.5 bg-[#7FD93C] text-black font-bold animate-pulse flex items-center gap-1" data-testid="live-banner"><span className="w-1.5 h-1.5 bg-black rounded-full"></span>EN DIRECT · P{game.period || 1}{game.clock ? ` · ${game.clock}` : ""}</span>
                                )}
                            </div>
                            <div className="font-display text-2xl tracking-wide">{formatDayLocalized(game.day, lang)} • {game.time} • {game.ice}</div>
                        </div>
                        <StatusBadge status={game.status} />
                    </div>
                    {(game.home_shots || game.away_shots) ? (
                        <div className="text-xs text-white/60 flex flex-wrap gap-4">
                            <span>TIRS : {game.home_shots || 0} <span className="text-white/30">—</span> {game.away_shots || 0}</span>
                            <span>PIM : {game.home_pim || 0} <span className="text-white/30">—</span> {game.away_pim || 0}</span>
                        </div>
                    ) : null}
                    <div className="mt-4">
                        <Link to={`/match/${num}/sommaire`} className="inline-flex items-center gap-2 text-[#4FE0D5] hover:text-[#7FD93C] text-sm" data-testid="link-summary"><FileText size={14} /> Voir le sommaire complet</Link>
                    </div>
                </div>

                {events.length > 0 && (
                    <div className="glass-card p-4 sm:p-5 mb-8" data-testid="match-events">
                        <div className="label-mini mb-3">Événements ({events.length})</div>
                        <div className="space-y-1.5">
                            {[...events].reverse().map(ev => {
                                const p = ev.player_id && playerMap[ev.player_id];
                                const a1 = ev.assist1_id && playerMap[ev.assist1_id];
                                const a2 = ev.assist2_id && playerMap[ev.assist2_id];
                                const icon = ev.type === "goal" ? "⚡" : ev.type === "penalty" ? "⚠" : "🎯";
                                return (
                                    <div key={ev.id} className="flex items-center gap-2 text-sm py-1 border-b border-white/5" data-testid={`match-event-${ev.id}`}>
                                        <span className="w-6 text-center shrink-0">{icon}</span>
                                        <span className="px-2 py-0.5 text-[10px] font-bold bg-white/5 shrink-0">P{ev.period}</span>
                                        {ev.time && <span className="text-[10px] font-mono text-white/60 shrink-0">{ev.time}</span>}
                                        <span className={`px-2 py-0.5 text-[10px] font-bold shrink-0 ${ev.team === "home" ? "bg-[#7FD93C]/20 text-[#7FD93C]" : "bg-[#4FE0D5]/20 text-[#4FE0D5]"}`}>{ev.team === "home" ? "LOC" : "VIS"}</span>
                                        <span className="flex-1 min-w-0 truncate text-white/80">
                                            {ev.type === "goal" && (p ? `${p.name} #${p.number}` : "But")}
                                            {ev.type === "penalty" && (p ? `${p.name} #${p.number} — ${ev.minutes || 0} min` : `Pénalité — ${ev.minutes || 0} min`)}
                                            {ev.type === "shot" && "Tir au but"}
                                            {(a1 || a2) && <span className="text-white/50 text-xs ml-1">(A: {a1?.name}{a2 ? `, ${a2.name}` : ""})</span>}
                                            {ev.reason && <span className="text-white/40 text-xs ml-1"> · {ev.reason}</span>}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                <div className="grid md:grid-cols-2 gap-6">
                    <TeamSide team={away} placeholder={game.away_placeholder} side="away" list={awayPlayers} score={game.away_score} />
                    <TeamSide team={home} placeholder={game.home_placeholder} side="home" list={homePlayers} score={game.home_score} />
                </div>
            </Section>
        </div>
    );
}
